import React from "react";
import PageHeader from "@/components/PageHeader";
import CtaBanner from "@/components/CtaBanner";
import { useLayout } from "@/components/Layout";
import { motion } from "framer-motion";
import { Briefcase, Users, Trophy, Calendar, Plane, Building2 } from "lucide-react";

const OFFERINGS = [
  { icon: Trophy, title: "Incentive Travel", text: "Reward top performers with unforgettable escapes — from Dubai desert galas to Swiss alpine retreats." },
  { icon: Users, title: "Team Offsites", text: "Purpose-built retreats that blend strategy sessions with signature cultural experiences." },
  { icon: Calendar, title: "Conferences & MICE", text: "End-to-end handling for meetings, incentives, conferences and exhibitions — from 20 to 2,000 delegates." },
  { icon: Plane, title: "Group Air Contracts", text: "Pre-negotiated group airfares and dedicated airport concierge for VIP arrivals." },
  { icon: Building2, title: "5-Star Room Blocks", text: "Preferred rates and complimentary upgrades across our global hotel network." },
  { icon: Briefcase, title: "On-ground Ops", text: "A dedicated ops team on the ground, from arrival to departure — nothing missed." },
];

const CLIENTS = ["Savera", "Balmukund", "Regent Cinema", "Sri RVSF", "Sarraf Real"];

export default function CorporatePage() {
  const { openPlan } = useLayout();
  return (
    <div data-testid="corporate-page">
      <PageHeader
        overline="Corporate Travel"
        title={"Reward. Convene. Inspire.\nAll in one flawless trip."}
        subtitle="From 20-person offsites to 2,000-delegate conferences, we design corporate experiences that raise the bar — with the discretion, precision and hospitality your team deserves."
        image="https://images.unsplash.com/photo-1517457373958-b7bdd4587205?q=80&w=2000&auto=format&fit=crop"
      />

      <section className="py-24 md:py-20 px-6 lg:px-10 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-end mb-14">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-px bg-[#C9A227]" />
                <span className="overline">Our Offerings</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-[#0A2342] leading-[1.05]">
                A full-service MICE partner.
              </h2>
            </div>
            <p className="text-[#6B7280] text-lg leading-relaxed">
              Recognised by India's largest corporates for consistent delivery, watertight logistics and an obsession with the small details that make a trip legendary.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {OFFERINGS.map((o, i) => (
              <motion.div
                key={o.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className="lux-card p-8"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#F8F9FB] flex items-center justify-center mb-5">
                  <o.icon className="w-5 h-5 text-[#C9A227]" />
                </div>
                <h3 className="font-display text-xl text-[#0A2342]">{o.title}</h3>
                <p className="mt-2 text-sm text-[#6B7280] leading-relaxed">{o.text}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-24">
            <div className="text-center overline mb-6">Trusted by leading Indian corporates</div>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-[#6B7280]">
              {CLIENTS.map((c) => (
                <span key={c} className="font-display text-2xl md:text-3xl opacity-60 hover:opacity-100 transition-opacity">{c}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaBanner onPlanTrip={() => openPlan()} />
    </div>
  );
}
